import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { FirebaseError } from "firebase/app";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { auth } from "../../../firebase";
import useToast from "../../../hooks/useToast";
import useAuthContext from "../../../hooks/useAuthContext";
import {
    clearOtpSession,
    markOtpRequired,
    setOtpSession,
} from "../../../utils/otpSessionStore";
import { useEncryptAuthFailureEmail } from "./auth.mutations";
import { fetchUserDetails, sendOtpCode } from "../services/auth.services";
import { IApiResponse } from "../../../utils/types/api-response";
import { IUser } from "../types/auth.types";
import { isIncompletePatientPortalAccountError } from "../helpers/auth.helpers";
import { USER_DETAILS_QUERY_KEY } from "../constants/auth.constants";

const OTP_COOLDOWN_SECONDS = 120;

interface ILoginProps {
    email: string;
    password: string;
}

const handleErrorMessageToShow = (errorCode: string) => {
    if (errorCode === "auth/wrong-password") {
        return "Your email or password is incorrect.";
    }

    if (errorCode === "auth/user-not-found") {
        return "We couldn't find an account with that email. Please check your email.";
    }

    return "Could not log you in at this time, please try again later.";
};

export default function useLoginUser() {
    const { toast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { logOutUser } = useAuthContext();
    const { mutateAsync: encryptEmail } = useEncryptAuthFailureEmail();

    const getEncryptedEmailForLogin = async (
        email: string,
        reason: string
    ): Promise<string | null> => {
        try {
            const response = await encryptEmail({
                email,
                failure_type: "login",
                metadata: {
                    pp_reason: reason,
                },
            });

            return response.encrypted_email;
        } catch (encryptError) {
            Sentry.logger.error("Failed to encrypt auth failure email", {
                failure_type: "login",
                reason,
                error_message:
                    encryptError instanceof Error
                        ? encryptError.message
                        : "Unknown encryptEmail error",
            });

            return null;
        }
    };

    const loginUser = async ({ email, password }: ILoginProps) => {
        try {
            const userCredential = await signInWithEmailAndPassword(
                auth,
                email,
                password
            );

            try {
                await queryClient.fetchQuery<IApiResponse<IUser>>({
                    queryKey: USER_DETAILS_QUERY_KEY,
                    queryFn: fetchUserDetails,
                    retry: false,
                });

                clearOtpSession(userCredential.user.uid);

                const from = localStorage.getItem("from");
                const safeTo =
                    from && from.startsWith("/") && !from.startsWith("//")
                        ? from
                        : "/";
                navigate(safeTo, { replace: true });
            } catch (error) {
                if (isIncompletePatientPortalAccountError(error)) {
                    navigate("/incomplete-account", { replace: true });
                    return;
                }

                if (
                    isAxiosError<IApiResponse<IUser>>(error) &&
                    error.response?.status === 428
                ) {
                    markOtpRequired(userCredential.user.uid);
                    const correlationId = crypto.randomUUID();
                    let otpChallengeId: string | undefined;

                    Sentry.logger.warn("Patient portal OTP required", {
                        area: "auth-bootstrap",
                        route: "/login",
                        source: "login-check",
                        otp_correlation_id: correlationId,
                    });

                    try {
                        Sentry.logger.info("Sending patient portal OTP", {
                            area: "login",
                            otp_correlation_id: correlationId,
                        });
                        const otpRes = await sendOtpCode({
                            email,
                            purpose: "login",
                            correlation_id: correlationId,
                        });

                        if (!otpRes?.challenge_id) {
                            throw new Error("No challenge_id returned");
                        }

                        otpChallengeId = otpRes.challenge_id;
                        setOtpSession(
                            userCredential.user.uid,
                            otpRes.challenge_id,
                            otpRes.otp_correlation_id,
                            OTP_COOLDOWN_SECONDS
                        );
                        Sentry.logger.info("Patient portal OTP sent", {
                            area: "login",
                            otp_correlation_id: correlationId,
                            challenge_id: otpRes.challenge_id,
                        });
                    } catch (otpError) {
                        Sentry.logger.warn("Couldn't auto-send code", {
                            area: "login",
                            otp_correlation_id: correlationId,
                            error_code: isAxiosError(otpError)
                                ? (otpError.code ?? "unknown")
                                : "unknown",
                            response_status: isAxiosError(otpError)
                                ? (otpError.response?.status ?? "none")
                                : "none",
                        });
                    }

                    Sentry.logger.info("Navigating to OTP verification", {
                        area: "login",
                        otp_correlation_id: correlationId,
                        challenge_id_obtained: Boolean(otpChallengeId),
                    });
                    navigate("/otp-verification", { replace: true });
                    return;
                }

                throw error;
            }
        } catch (error) {
            if (error instanceof FirebaseError) {
                toast({
                    mode: "error",
                    message: handleErrorMessageToShow(error.code),
                });

                const encryptedEmail = await getEncryptedEmailForLogin(
                    email,
                    error.code
                );

                if (encryptedEmail) {
                    Sentry.logger.error("Email/password login failed", {
                        encrypted_email: encryptedEmail,
                        failure_type: "login",
                        reason: error.code,
                    });
                }

                return;
            }

            toast({
                mode: "error",
                message:
                    error instanceof Error
                        ? error.message
                        : "Could not log you in at this time, please try again later",
            });

            await logOutUser();
        }
    };

    return {
        loginUser,
    };
}
